import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Gift, Eye, EyeOff, User, Phone, Cake, Lock, ArrowRight, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

export default function LoyaltyRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    birthday_day: '',
    birthday_month: '',
    password: '',
    referral_code: referralCode || '',
    branch_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('loyalty_token');
    if (token) {
      navigate('/lealtad');
    }
    
    // Load branches
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches/public`);
      setBranches(response.data);
    } catch (error) {
      // error handled by toast
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.whatsapp || !formData.birthday_day || !formData.birthday_month || !formData.password) {
      toast.error('Completa todos los campos');
      return;
    }

    if (formData.whatsapp.length < 10) {
      toast.error('WhatsApp debe tener al menos 10 dígitos');
      return;
    }

    if (formData.password.length < 4) {
      toast.error('La contraseña debe tener al menos 4 caracteres');
      return;
    }

    if (!formData.branch_id) {
      toast.error('Selecciona tu sucursal preferida');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/loyalty/register`, {
        name: formData.name,
        whatsapp: formData.whatsapp,
        birthday_day: parseInt(formData.birthday_day),
        birthday_month: parseInt(formData.birthday_month),
        password: formData.password,
        referral_code: formData.referral_code || null,
        branch_id: formData.branch_id,
      });
      
      toast.success(`¡Bienvenido! Tu código es: ${response.data.user.user_code}`);
      localStorage.setItem('loyalty_token', response.data.token);
      navigate('/lealtad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="loyalty-register">
      {/* Header */}
      <header className="py-8 text-center">
        <Gift className="w-12 h-12 text-gold mx-auto mb-4" />
        <h1 className="font-heading text-3xl text-white mb-2">Programa de Lealtad</h1>
        <p className="text-zinc-500">Regístrate y gana puntos en cada visita</p>
      </header>

      {/* Form */}
      <main className="flex-1 flex items-start justify-center px-4 pb-8">
        <Card className="w-full max-w-md bg-zinc-900/80 border-zinc-800 p-6">
          {referralCode && (
            <div className="mb-6 p-3 bg-gold/10 border border-gold/30 rounded-sm">
              <p className="text-sm text-gold">
                ¡Te han referido! Recibirás puntos de bienvenida al registrarte.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <User className="w-4 h-4" />
                Nombre Completo
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Tu nombre"
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                data-testid="register-name-input"
              />
            </div>

            {/* WhatsApp */}
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                WhatsApp (10 dígitos)
              </Label>
              <Input
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value.replace(/\D/g, '') })}
                placeholder="5512345678"
                maxLength={10}
                className="bg-zinc-800 border-zinc-700 text-white h-12"
                data-testid="register-whatsapp-input"
              />
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <Cake className="w-4 h-4" />
                Fecha de Cumpleaños
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Select 
                  value={formData.birthday_day} 
                  onValueChange={(value) => setFormData({ ...formData, birthday_day: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12">
                    <SelectValue placeholder="Día" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {Array.from({ length: 31 }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()} className="text-white">
                        {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={formData.birthday_month} 
                  onValueChange={(value) => setFormData({ ...formData, birthday_month: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white h-12">
                    <SelectValue placeholder="Mes" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {MONTHS.map((month) => (
                      <SelectItem key={month.value} value={month.value} className="text-white">
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Branch Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Sucursal Preferida
              </Label>
              <Select 
                value={formData.branch_id} 
                onValueChange={(value) => setFormData({ ...formData, branch_id: value })}
              >
                <SelectTrigger 
                  className="bg-zinc-800 border-zinc-700 text-white h-12"
                  data-testid="register-branch-select"
                >
                  <SelectValue placeholder="Selecciona tu sucursal" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id} className="text-white">
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label className="text-zinc-400 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Contraseña
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Mínimo 4 caracteres"
                  className="bg-zinc-800 border-zinc-700 text-white h-12 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Referral Code */}
            <div className="space-y-2">
              <Label className="text-zinc-400">Código de Referido (opcional)</Label>
              <Input
                value={formData.referral_code}
                onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                placeholder="ABC"
                maxLength={3}
                className="bg-zinc-800 border-zinc-700 text-white h-12 uppercase font-mono"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 bg-gold text-black hover:bg-gold-dark font-semibold uppercase tracking-wide"
              data-testid="register-submit-btn"
            >
              {loading ? 'Registrando...' : (
                <>
                  Crear mi Cuenta
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              ¿Ya tienes cuenta?{' '}
              <Link to="/lealtad/login" className="text-gold hover:underline">
                Inicia sesión
              </Link>
            </p>
          </div>
        </Card>
      </main>

      {/* Benefits */}
      <div className="max-w-md mx-auto px-4 pb-8">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-2">
              <Gift className="w-5 h-5 text-gold" />
            </div>
            <p className="text-xs text-zinc-500">Gana puntos por visita</p>
          </div>
          <div>
            <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-2">
              <Gift className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-xs text-zinc-500">Canjea recompensas</p>
          </div>
          <div>
            <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-2">
              <User className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-xs text-zinc-500">Refiere amigos</p>
          </div>
        </div>
      </div>
    </div>
  );
}
